import { useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { useCarspend, carsInBackup } from "./useCarspend.ts";
import { useEntitlement } from "./useEntitlement.ts";
import { ImportPanel } from "./ImportPanel.tsx";
import { Paywall } from "./Paywall.tsx";
import { GarageView } from "./views/GarageView.tsx";
import { FuelView } from "./views/FuelView.tsx";
import { SpendView } from "./views/SpendView.tsx";
import { TripsView } from "./views/TripsView.tsx";
import { TyresView } from "./views/TyresView.tsx";
import { SettingsView } from "./views/SettingsView.tsx";
import { CustomThemeView } from "./views/CustomThemeView.tsx";
import { IconGarage, IconFuel, IconSpend, IconSettings } from "./components/Icons.tsx";
import { BackRow } from "./forms/Modal.tsx";
import { loadTheme, applyTheme, type Palette } from "./themes.ts";

type Tab = "garage" | "fuel" | "spend" | "settings" | "trips" | "tyres" | "custom-theme";
const NAV: { id: Tab; label: string; icon: ReactNode }[] = [
  { id: "fuel", label: "FUEL", icon: <IconFuel /> },
  { id: "spend", label: "SPEND", icon: <IconSpend /> },
  { id: "garage", label: "GARAGE", icon: <IconGarage /> },
  { id: "settings", label: "SETTINGS", icon: <IconSettings /> },
];

export function App() {
  const { loading, store, importBackup, exportBackup, clearAll, save, lastExportAt, loadDemo } = useCarspend();
  const { isPro, redeem, pairId } = useEntitlement();
  const [tab, setTab] = useState<Tab>("garage");
  const [carId, setCarId] = useState<string>("");
  const [showPaywall, setShowPaywall] = useState(false);
  const requirePro = () => setShowPaywall(true);
  const [importCars, setImportCars] = useState<{ text: string; attachments?: Map<string, Uint8Array>; cars: { id: string; name: string }[] } | null>(null);
  const [importMsg, setImportMsg] = useState<{ skipped: number; reasons: string[] } | null>(null);
  const [theme, setThemeState] = useState<Palette>(() => { const t = loadTheme(); applyTheme(t); return t; });

  useLayoutEffect(() => { applyTheme(theme); }, [theme]);

  // Wrapper that intercepts multi-car import for free users, and surfaces a
  // persistent result banner (the import screen unmounts once data lands).
  const onImport = useCallback(async (fileText: string, attachments?: Map<string, Uint8Array>, carIdArg?: string) => {
    if (!isPro && !carIdArg) {
      const cars = carsInBackup(fileText);
      if (cars.length > 1) {
        setImportCars({ text: fileText, attachments, cars });
        return { imported: false, skipped: 0, reasons: [] };
      }
    }
    const res = await importBackup(fileText, attachments, carIdArg);
    if (res.imported) setImportMsg({ skipped: res.skipped, reasons: res.reasons });
    return res;
  }, [isPro, importBackup]);

  useEffect(() => {
    if (store && store.cars.length && !store.cars.some((c) => c.id === carId)) setCarId(store.cars[0]!.id);
  }, [store, carId]);

  if (loading) {
    return <div className="app" style={{ alignItems: "center", justifyContent: "center", display: "flex" }}>
      <div className="mono muted">Loading…</div>
    </div>;
  }
  if (!store || store.cars.length === 0) {
    return <div className="app"><ImportPanel hasData={false} isPro={isPro} onImport={onImport} onLoadDemo={loadDemo} /></div>;
  }

  // Free tier: one car only (mirrors mobile). Pro: the selected car.
  const car = (isPro ? store.cars.find((c) => c.id === carId) : store.cars[0]) ?? store.cars[0]!;
  const exportDays = lastExportAt != null ? Math.floor((Date.now() / 1000 - lastExportAt) / 86400) : null;
  const showBackupNudge = isPro && (exportDays == null || exportDays >= 14);
  const navActive: Tab = (tab === "trips" || tab === "tyres") ? "garage" : tab;

  return (
    <div className="app">
      <header className="container topbar" onClick={() => setTab("fuel")} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28,
            mask: "url(./carspend-icon.svg) center/contain no-repeat",
            WebkitMask: "url(./carspend-icon.svg) center/contain no-repeat",
            background: "var(--icon)",
          }} />
          <div className="mono" style={{ fontSize: 15, letterSpacing: 3, color: "var(--cream)" }}>CAR<span className="amber">SPEND</span></div>
        </div>
      </header>

      {importMsg && (
        <div className="container" style={{ paddingTop: 2 }}>
          <div className="panel" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderColor: importMsg.skipped > 0 ? "var(--amberDim)" : "var(--lime)" }}>
            <span className="mono" style={{ fontSize: 11, color: "var(--cream)" }}>
              <span className="lime">✓</span> Import complete{importMsg.skipped > 0 ? ` — ${importMsg.skipped} record${importMsg.skipped === 1 ? "" : "s"} skipped` : ". All records imported."}
            </span>
            <button className="btn ghost" style={{ fontSize: 10, padding: "4px 10px" }} onClick={() => setImportMsg(null)}>DISMISS</button>
          </div>
        </div>
      )}

      {showBackupNudge && tab !== "settings" && (
        <div className="container" style={{ paddingTop: 2 }}>
          <button className="linkrow" style={{ borderColor: "var(--amberDim)", justifyContent: "space-between" }} onClick={exportBackup}>
            <span className="mono muted" style={{ fontSize: 10 }}>
              {exportDays == null ? "No backup yet — your data lives only in this browser." : `Last backup ${exportDays} days ago.`}
            </span>
            <span className="mono amber" style={{ fontSize: 10 }}>EXPORT →</span>
          </button>
        </div>
      )}

      <main>
        {tab === "garage" && <GarageView store={store} selectedId={car.id} onSelect={setCarId} save={save} isPro={isPro} requirePro={requirePro} onOpenTrips={() => setTab("trips")} onOpenTyres={() => setTab("tyres")} />}
        {tab === "fuel" && <FuelView store={store} car={car} save={save} isPro={isPro} requirePro={requirePro} onOpenSpend={() => setTab("spend")} />}
        {tab === "spend" && <SpendView store={store} car={car} save={save} />}
        {tab === "trips" && <TripsView store={store} car={car} save={save} onBack={() => setTab("garage")} />}
        {tab === "tyres" && <TyresView store={store} car={car} save={save} onBack={() => setTab("garage")} />}
        {tab === "settings" && (
          <SettingsView
            store={store} save={save} isPro={isPro} lastExportAt={lastExportAt} requirePro={requirePro}
            onImport={onImport} onExport={exportBackup} onUnlock={requirePro}
            onClear={() => void clearAll()} theme={theme} setTheme={setThemeState}
            onOpenCustomTheme={() => setTab("custom-theme")}
          />
        )}
        {tab === "custom-theme" && (
          <CustomThemeView isPro={isPro} requirePro={requirePro} theme={theme} setTheme={setThemeState} onBack={() => setTab("settings")} />
        )}
      </main>

      <nav className="nav">
        <div className="nav-inner">
          {NAV.map((t) => (
            <button key={t.id} className={`navbtn${navActive === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>
              <span className="ic">{t.icon}</span>
              <span className="lbl">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {showPaywall && <Paywall onClose={() => setShowPaywall(false)} onRedeem={redeem} pairId={pairId} />}

      {importCars && (
        <div className="screen" style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="container" style={{ maxWidth: 420 }}>
            <div className="panel">
              <div className="mono amber" style={{ fontSize: 11, letterSpacing: 1.6 }}>FREE TIER · ONE CAR ONLY</div>
              <p className="mono" style={{ color: "var(--creamDim)", fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
                Your backup contains <span className="amber">{importCars.cars.length} cars</span>.
                Pick which one to import:
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                {importCars.cars.map((c) => (
                  <button key={c.id} className="panel" style={{ textAlign: "left", cursor: "pointer" }} onClick={async () => { const text = importCars.text; const att = importCars.attachments; setImportCars(null); const res = await importBackup(text, att, c.id); if (res.imported) setImportMsg({ skipped: res.skipped, reasons: res.reasons }); }}>
                    <span className="mono" style={{ color: "var(--cream)", fontSize: 13 }}>{c.name}</span>
                  </button>
                ))}
                <button className="btn ghost" style={{ marginTop: 8 }} onClick={() => setImportCars(null)}>CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
